import React, { useEffect, useRef, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";

/* ─── helpers ───────────────────────────────────────────────────── */

/** Parse "0d 70 00 00 00 00 00 00" → ["0d","70","00","00","00","00","00","00"] */
function parseBytes(dataStr = "") {
  return dataStr.trim().split(/\s+/).filter(Boolean);
}

const MAX_BYTES = 8;

/* ─── sub-component: one byte cell with optional pulse ──────────── */
function ByteCell({ value, pulsing }) {
  return (
    <td
      className={`p-2 font-mono text-center transition-all duration-300 ${pulsing ? "byte-pulse" : ""
        }`}
    >
      {value ?? "-"}
    </td>
  );
}

/* ─── main component ────────────────────────────────────────────── */
export default function RawDataTable({ brand, model }) {
  // rowMap: { [can_id]: { can_id, dlc, bytes: string[], lastSeenAt, pulseMask: bool[] } }
  const [rowMap, setRowMap] = useState({});
  const [changeLogs, setChangeLogs] = useState([]);
  const pulseTimers = useRef({});

  useEffect(() => {
    const ref = collection(db, "EIS", brand, "Models", model, "RawData");

    return onSnapshot(ref, snap => {
      setRowMap(prev => {
        const next = { ...prev };

        snap.docs.forEach(d => {
          const incoming = { id: d.id, ...d.data() };
          const canId = incoming.can_id ?? d.id;
          const newBytes = parseBytes(incoming.data);
          const ts = incoming.lastSeenAt?.toDate?.()?.toLocaleString() ?? new Date().toLocaleString();

          if (next[canId]) {
            // ── already exists → diff bytes ──────────────────────
            const oldBytes = next[canId].bytes;
            const pulseMask = newBytes.map((b, i) => b !== (oldBytes[i] ?? ""));
            const hasChange = pulseMask.some(Boolean);

            if (hasChange) {
              // log the change
              setChangeLogs(logs => [
                {
                  key: `${canId}-${Date.now()}`,
                  timestamp: ts,
                  canId,
                  oldBytes: [...oldBytes],
                  newBytes: [...newBytes],
                },
                ...logs,
              ]);

              // set pulse, then clear after 1.2 s
              next[canId] = { ...next[canId], bytes: newBytes, pulseMask, lastSeenAt: incoming.lastSeenAt };

              clearTimeout(pulseTimers.current[canId]);
              pulseTimers.current[canId] = setTimeout(() => {
                setRowMap(cur => {
                  if (!cur[canId]) return cur;
                  return {
                    ...cur,
                    [canId]: { ...cur[canId], pulseMask: Array(MAX_BYTES).fill(false) },
                  };
                });
              }, 1200);
            } else {
              next[canId] = { ...next[canId], bytes: newBytes, lastSeenAt: incoming.lastSeenAt };
            }
          } else {
            // ── brand new row ────────────────────────────────────
            next[canId] = {
              canId,
              dlc: incoming.dlc,
              bytes: newBytes,
              lastSeenAt: incoming.lastSeenAt,
              pulseMask: Array(MAX_BYTES).fill(false),
            };
          }
        });

        return next;
      });
    });
  }, [brand, model]);

  const rows = Object.values(rowMap).sort((a, b) =>
    (a.canId ?? "").localeCompare(b.canId ?? "")
  );

  return (
    <div className="space-y-6">
      {/* ── pulse keyframe style ── */}
      <style>{`
        @keyframes bytePulse {
          0%   { background-color: #bbf7d0; box-shadow: 0 0 0 0 rgba(34,197,94,0.6); }
          50%  { background-color: #4ade80; box-shadow: 0 0 8px 4px rgba(34,197,94,0.35); }
          100% { background-color: transparent; box-shadow: none; }
        }
        .byte-pulse { animation: bytePulse 1.2s ease-out forwards; border-radius: 4px; }
      `}</style>

      {/* ── main table ── */}
      {rows.length === 0 ? (
        <div className="text-slate-400 text-sm">No raw CAN data yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-xl shadow text-xs border-collapse">
            <thead className="bg-slate-100 text-slate-500 uppercase">
              <tr>
                <th className="p-3 text-left whitespace-nowrap">CAN ID</th>
                <th className="p-3 text-center">DLC</th>
                {Array.from({ length: MAX_BYTES }, (_, i) => (
                  <th key={i} className="p-3 text-center whitespace-nowrap">
                    Byte {i + 1}
                  </th>
                ))}
                <th className="p-3 text-left whitespace-nowrap">Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.canId} className="border-t hover:bg-slate-50">
                  <td className="p-3 font-mono font-semibold text-slate-700">{r.canId}</td>
                  <td className="p-3 text-center text-slate-500">{r.dlc ?? "-"}</td>
                  {Array.from({ length: MAX_BYTES }, (_, i) => (
                    <ByteCell
                      key={i}
                      value={r.bytes[i]}
                      pulsing={r.pulseMask?.[i] ?? false}
                    />
                  ))}
                  <td className="p-3 text-slate-400 whitespace-nowrap">
                    {r.lastSeenAt?.toDate?.().toLocaleString() ?? "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── change log section ── */}
      {changeLogs.length > 0 && (
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              Byte Change Log
            </h3>
            <button
              onClick={() => setChangeLogs([])}
              className="text-xs text-slate-400 hover:text-red-500 transition-colors"
            >
              Clear
            </button>
          </div>

          <div className="overflow-x-auto max-h-60 overflow-y-auto">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                <tr>
                  <th className="p-2 text-left whitespace-nowrap">Timestamp</th>
                  <th className="p-2 text-center whitespace-nowrap">CAN ID</th>
                  {Array.from({ length: MAX_BYTES }, (_, i) => (
                    <th key={i} className="p-2 text-center whitespace-nowrap">
                      B{i + 1} Old
                    </th>
                  ))}
                  {Array.from({ length: MAX_BYTES }, (_, i) => (
                    <th key={i} className="p-2 text-center whitespace-nowrap">
                      B{i + 1} New
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {changeLogs.map(log => {
                  const diffMask = log.newBytes.map((b, i) => b !== (log.oldBytes[i] ?? ""));
                  return (
                    <tr key={log.key} className="border-t font-mono">
                      <td className="p-2 text-slate-400 whitespace-nowrap">{log.timestamp}</td>
                      <td className="p-2 text-center font-semibold text-slate-700">{log.canId}</td>
                      {Array.from({ length: MAX_BYTES }, (_, i) => (
                        <td
                          key={i}
                          className={`p-2 text-center ${diffMask[i] ? "text-red-500 font-bold" : "text-slate-500"
                            }`}
                        >
                          {log.oldBytes[i] ?? "-"}
                        </td>
                      ))}
                      {Array.from({ length: MAX_BYTES }, (_, i) => (
                        <td
                          key={i}
                          className={`p-2 text-center ${diffMask[i] ? "text-green-600 font-bold" : "text-slate-500"
                            }`}
                        >
                          {log.newBytes[i] ?? "-"}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
